/**
 * The one list of INTERNAL surfaces — FRIDGE-418.
 *
 * docs/Page-Types-And-Templates.md has always said these are internal and
 * "excluded by mandate and NOT page types of the site". Two separate defects
 * came from that being written in prose and nowhere in code:
 *
 *   1. `/_qa/*` was reachable by anyone. Measured on a production build with no
 *      cookie on 08/18/2026: `/_qa/es/home` returned 200 with 458 KB of
 *      rendered page, as did `/_qa/es/marca/lg` and `/_qa/es/coleccion/…`.
 *      It carried `X-Robots-Tag: noindex` and nothing else — and noindex is not
 *      access control. `middleware.ts` now gates it on the admin cookie.
 *
 *   2. The newsletter capture popup fired on `/operations/*`. Its exclusion
 *      list held only `/contact`, `/privacy-policy`, `/terms-of-service`, `/r`
 *      and `/embed`, so every internal admin page fell through to the general
 *      variant and an email-capture card rendered over the operations
 *      dashboards. Observed directly on `/operations/analytics` and
 *      `/operations/seo`.
 *
 * Both fixes read THIS list, so a new internal namespace cannot be gated in one
 * place and forgotten in the other. Adding a prefix here closes it everywhere.
 *
 *   3. An escaped spelling walked past the gate (FRIDGE-1206): `/%61dmin-panel`
 *      served the Admin Panel because the prefix test read the raw path while
 *      the router decoded it. Every prefix test now reads `gatePathname()` as
 *      well — see `isUnderGatedPrefix` below.
 *
 * Not in this list on purpose:
 *   • `/mockups/*` — internal, but already 404s in production via
 *     `app/mockups/layout.tsx` (`NODE_ENV === 'production' → notFound()`), so
 *     it is unreachable rather than gated. It IS listed for the popup rule
 *     below, because it is reachable in development and a capture card there is
 *     the same noise.
 *   • `/api/*` — guarded separately in middleware.ts; these are page prefixes.
 */

/** Page prefixes that must never be reachable without the admin cookie. */
export const AUTH_GATED_PREFIXES = ['/admin-panel', '/operations', '/_qa'] as const

/**
 * Page prefixes that are internal tooling, whether or not they are auth-gated.
 * No marketing surface — popup, capture bar, promotional interstitial — may
 * render on any of these.
 */
export const INTERNAL_SURFACE_PREFIXES = [
  ...AUTH_GATED_PREFIXES,
  '/mockups',
  '/docs',
] as const

/**
 * The path the router will serve, for judging a gate — FRIDGE-1206.
 *
 * The platform decodes percent-escaped characters before it picks a route,
 * but `request.nextUrl.pathname` keeps them as written. Measured on
 * production 10/17/2026 with no credentials: `/%61bout-us` served About Us,
 * and `/%61dmin-panel`, `/admin%2Dpanel` and `/%61dmin-panel/offers` served the
 * Admin Panel screens with a 200 while `/admin-panel` redirected to the login
 * page — a prefix test on the raw path never saw the gated prefix. Gates
 * compare this decoded form as well as the raw one.
 *
 * Each segment is decoded on its own, so a malformed escape in one segment
 * neither throws nor stops the rest of the path from being judged; that
 * segment is kept as written. An escaped slash becomes a separator, which can
 * only widen what a gate covers. Decoding repeats up to a fixed bound, so a
 * double-escaped spelling is judged like the plain one — stricter than the
 * router, which decodes once and never serves those as the gated route.
 */
const GATE_DECODE_ROUNDS = 5

function decodeSegment(segment: string): string {
  if (!segment.includes('%')) return segment
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

export function gatePathname(pathname: string): string {
  let current = pathname
  for (let round = 0; round < GATE_DECODE_ROUNDS && current.includes('%'); round++) {
    const next = current.split('/').map(decodeSegment).join('/')
    if (next === current) break
    current = next
  }
  return current
}

/** Is `pathname` — as written, or as the router will decode it — at or under `prefix`? */
export function isUnderGatedPrefix(pathname: string, prefix: string): boolean {
  return [pathname, gatePathname(pathname)].some((p) => p === prefix || p.startsWith(prefix + '/'))
}

/** Does this path sit under an internal surface? */
export function isInternalSurface(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return INTERNAL_SURFACE_PREFIXES.some((p) => isUnderGatedPrefix(pathname, p))
}
